import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { MONTHLY_PRICE, YEARLY_PRICE, YEARLY_SAVING } from '../helpers/pricingPlans';
import { PLAN_CTAS, usePlanViewer } from '../hooks/usePlanViewer';

const PLAN_FEATURES = [
    { icon: 'fa-eye', label: 'Match Overview', free: true },
    { icon: 'fa-chart-pie', label: 'Match Predictions (1X2)', free: true },
    { icon: 'fa-gauge-high', label: 'Correct Score Predictions', free: false },
    { icon: 'fa-arrow-trend-up', label: 'Confidence Breakdowns', free: false },
    { icon: 'fa-list-check', label: 'Over / Under Predictions', free: false },
    { icon: 'fa-futbol', label: 'BTTS Predictions', free: false },
    { icon: 'fa-star', label: 'Goal Scorer Predictions', free: false },
    { icon: 'fa-users', label: 'Head-to-Head Performance', free: false },
    { icon: 'fa-monitor-waveform', label: 'Team Performance Analysis', free: false },
    { icon: 'fa-binoculars', label: 'Player Intelligence', free: false },
    { icon: 'fa-newspaper', label: 'Match News', free: true },
    { icon: 'fa-robot', label: 'AI Match Intelligence', free: false },
    { icon: 'fa-arrows-rotate', label: 'Prediction Updates', free: false },
];

const PLAN_BOXES = [
    { key: 'yearly', head: 'Twelve months, best value', price: YEARLY_PRICE, unit: '/ year', note: `Save £${YEARLY_SAVING} vs monthly`, billing: 'Billed annually. Cancel anytime.', href: '/sign-up?plan=yearly' },
    { key: 'monthly', head: 'Month to month', price: MONTHLY_PRICE, unit: '/ month', note: 'Flexible, cancel anytime', billing: 'Billed monthly. Cancel anytime.', href: '/sign-up?plan=monthly' },
];

const PREMIUM_UNLOCKS = [
    { icon: 'fa-chart-pie', title: 'Full prediction markets', body: 'Correct Score, Over/Under, BTTS & Goal Scorers' },
    { icon: 'fa-magnifying-glass', title: 'Deeper match intelligence', body: 'Head-to-Head and Team Performance analysis' },
    { icon: 'fa-robot', title: 'AI Match Intelligence', body: 'AI-powered analysis combining football data and news' },
];

const REASSURANCE = [
    { icon: 'fa-circle-check', title: 'No commitment', body: 'Cancel anytime.' },
    // fa-light fa-lock isn't used on any live page yet (the subset kit would ship it blank),
    // so the proven shield stands in for the padlock.
    { icon: 'fa-shield-halved', title: 'Secure payments', body: 'Safe and protected.' },
];

const Tick = ({ on }) => (on
    ? <i className="fa-light fa-check matrix-yes" aria-label="Included"></i>
    : <span className="matrix-no" aria-label="Not included">&ndash;</span>
);

const PricingCompare = ({ id = 'pricing' }) => {
    // Same viewer rules as the /pricing top cards (see usePlanViewer): logged-in viewers get
    // current / switch / upgrade buttons; guests the default trial CTAs.
    const viewer = usePlanViewer();
    const ctas = PLAN_CTAS[viewer];

    return (
    <section id={id} className='sk-pricing'>
        <div className="container max-w-1200">
            <div className="mb-4 reveal">
                <div className="section-label"><span className="dot"></span> Compare plans</div>
                <h2 className="section-heading fs-44 fw-extra-bold lh-115 ls-n044 mt-3 mb-2">
                    Start free. <span className="gradient-text">Unlock the full match.</span>
                </h2>
                <p className="fs-18 text-sk-light lh-145 mb-0">
                    See what you get for free, and what you unlock with SportsKinetic Premium.
                </p>
            </div>
            <div className="row g-4 align-items-stretch">
                <div className="col-lg-7 d-flex reveal-left">
                    <div className="guide-table-wrap plan-matrix-wrap flex-grow-1">
                        <table className="guide-table compare plan-matrix h-100">
                            <thead>
                                <tr>
                                    <th>Features</th>
                                    <th className="text-center">Free</th>
                                    <th className="text-center">
                                        Monthly
                                        <span className="matrix-price">&pound;{MONTHLY_PRICE}/month</span>
                                    </th>
                                    <th className="text-center position-relative">
                                        <span className="matrix-ribbon">Best value</span>
                                        <span className="d-block"><i className="fa-light fa-star me-1" aria-hidden="true"></i>Yearly</span>
                                        <span className="matrix-price">&pound;{YEARLY_PRICE}/year</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {PLAN_FEATURES.map((row) => (
                                    <tr key={row.label}>
                                        <td>
                                            <span className="d-flex align-items-center gap-2">
                                                <i className={`fa-light ${row.icon} matrix-icon`} aria-hidden="true"></i>
                                                {row.label}
                                            </span>
                                        </td>
                                        <td className="text-center"><Tick on={row.free} /></td>
                                        <td className="text-center"><Tick on /></td>
                                        <td className="text-center"><Tick on /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="col-lg-5 reveal-right">
                    <div className="premium-panel h-100 p-md-4 p-3 d-flex flex-column gap-3">
                        {/* Trust line for both plans, stated once above them (sign-up wording,
                            so only for viewers who haven't subscribed). */}
                        {!ctas && (
                            <p className="fs-13 fw-semi-bold text-sk-light text-center mb-0">
                                Card required &middot; <span className="text-sk-green">&pound;0 today</span> &middot; Cancel anytime
                            </p>
                        )}
                        {PLAN_BOXES.map((plan) => {
                            const cta = ctas?.[plan.key];
                            return (
                            <div key={plan.key} className={`premium-plan premium-plan--${plan.key}${cta?.kind === 'current' ? ' premium-plan--current' : ''}`}>
                                <div className="premium-plan-head">
                                    {plan.key === 'yearly' && <i className="fa-light fa-star me-1" aria-hidden="true"></i>}
                                    {plan.head}
                                </div>
                                <div className="premium-plan-body d-flex flex-wrap flex-xl-nowrap align-items-center justify-content-between gap-3">
                                    <div>
                                        <div className="d-flex align-items-baseline gap-1">
                                            <span className="premium-plan-price">&pound;{plan.price}</span>
                                            <span className="fs-13 text-sk-light text-nowrap">{plan.unit}</span>
                                        </div>
                                        <p className="premium-plan-note fs-13 fw-semi-bold mt-2 mb-0">{plan.note}</p>
                                    </div>
                                    <div className="premium-plan-action">
                                        {cta?.kind === 'current' ? (
                                            // Status, not an action: plain element, not clickable.
                                            <span className="premium-plan-cta premium-plan-cta--current text-center d-block" aria-current="true">
                                                {cta.label}
                                            </span>
                                        ) : (
                                            <Link to={cta?.href || plan.href} className="btn-sk-primary premium-plan-cta text-white text-center d-block text-decoration-none">
                                                <span>{cta?.label || 'Start 7-day free trial'}</span>
                                            </Link>
                                        )}
                                        <p className="fs-12 text-sk-body text-center mt-2 mb-0">{plan.billing}</p>
                                    </div>
                                </div>
                            </div>
                            );
                        })}

                        {/* Thin full-width rows that share whatever height is left, so there's
                            no gap above the footer. */}
                        <div className="d-flex flex-column flex-grow-1">
                            <div className="fs-14 fw-bold text-white mb-2">What Premium unlocks</div>
                            <div className="premium-unlock-grid d-grid grid-gap-2 flex-grow-1">
                                {PREMIUM_UNLOCKS.map((item) => (
                                    <div key={item.title} className="premium-unlock d-flex align-items-center gap-3">
                                        <span className="premium-unlock-icon flex-shrink-0">
                                            <i className={`fa-light ${item.icon}`} aria-hidden="true"></i>
                                        </span>
                                        <div>
                                            <div className="fs-14 fw-semi-bold text-white">{item.title}</div>
                                            <div className="fs-12 text-sk-body lh-15">{item.body}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Items in equal slots with a "|" divider element between neighbours;
                            stacks on phones. */}
                        <div className="premium-reassure d-flex flex-column flex-md-row align-items-md-center gap-3">
                            {REASSURANCE.map((item, idx) => (
                                <Fragment key={item.title}>
                                    {idx > 0 && <span className="premium-reassure-divider d-none d-md-block" aria-hidden="true"></span>}
                                    <div className="premium-reassure-item d-flex align-items-center gap-2 justify-content-md-center">
                                        <span className="premium-reassure-icon flex-shrink-0">
                                            <i className={`fa-light ${item.icon}`} aria-hidden="true"></i>
                                        </span>
                                        <div>
                                            <div className="fs-13 fw-semi-bold text-white">{item.title}</div>
                                            <div className="fs-12 text-sk-body">{item.body}</div>
                                        </div>
                                    </div>
                                </Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
    );
};

export default PricingCompare;
